'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { MAX_DICTATION_SESSION_MINUTES } from '@/lib/dictation/session-limits'
import type {
  DictationSession,
  TranscriptMark,
} from '@/lib/dictation/stream-dictation'
import { reportBrowserError } from '@/lib/observability/datadog-rum'
import { appToast } from '@/lib/shared/toast'

export type DictationStatus = 'idle' | 'starting' | 'listening' | 'finishing'

/**
 * The editor end of a dictation session. The prompt editor registers one and
 * the session writes into it; the editor owns HOW text lands, because it has
 * to keep mention chips intact.
 */
export type DictationTarget = {
  /** Snapshot the prompt; dictated text is shown after the snapshot. */
  begin(): void
  /** Show `text` — the whole dictation so far — after the snapshot. */
  render(text: string): void
  /** Leave dictation mode; the prompt keeps whatever it shows. */
  end(): void
}

export type DictationHold = {
  /** The send was declined (or the caret is back): held speech lands. */
  release(): void
  /** The send went out: end that session without showing held speech. */
  cancel(): void
}

/** A send deciding, or the caret sitting mid-text while the user edits. */
export type DictationHoldReason = 'send' | 'caret'

type DictatedText = { text: string; mark: TranscriptMark }

const SETTLED_HOLD: DictationHold = {
  release: () => undefined,
  cancel: () => undefined,
}

export type PromptInputDictation = {
  status: DictationStatus
  /** A prompt editor is mounted and can receive dictation. */
  isAvailable: boolean
  start(): void
  stop(): void
  /** Drop the session now (the prompt is being sent). */
  cancel(): void
  /**
   * Keep listening, but show nothing new until the returned handle settles
   * it: a send in flight keeps the prompt exactly the text being sent, and a
   * mid-text caret keeps the editor from jumping it to the end. The handle
   * only acts on the session that was live when it was taken — a session
   * started afterwards is not its to end.
   */
  hold(reason: DictationHoldReason): DictationHold
  /** The user edited the prompt mid-dictation: what they see is theirs. */
  rebase(): void
  registerTarget(target: DictationTarget | null): void
}

const DictationContext = createContext<PromptInputDictation | null>(null)

export const PromptInputDictationProvider = DictationContext.Provider

export function usePromptInputDictation(): PromptInputDictation | null {
  return useContext(DictationContext)
}

const MICROPHONE_BLOCKED_COPY =
  "Microphone access is blocked. Allow it in your browser's site settings to dictate."

function describeStartError(error: unknown): string | null {
  if (!(error instanceof Error)) {
    return null
  }
  if (error.name === 'MicrophoneAccessError') {
    const { reason } = error as Error & { reason?: string }
    if (reason === 'denied') {
      return MICROPHONE_BLOCKED_COPY
    }
    return reason === 'busy'
      ? 'Your microphone is in use by another app.'
      : 'No microphone was found.'
  }
  if (error.name === 'DictationTokenError') {
    const { status } = error as Error & { status?: number }
    // The route's own copy for credits, role and rate limits.
    if (status === 402 || status === 403 || status === 429) {
      return error.message
    }
  }
  return null
}

/**
 * One composer's dictation state machine: idle → starting → listening →
 * finishing → idle. Every async callback is tagged with the session it
 * belongs to, so a stop, a send or an unmount makes a late token, transcript
 * part or error from an abandoned session inert.
 */
export function usePromptInputDictationController(): PromptInputDictation {
  const [status, setStatus] = useState<DictationStatus>('idle')
  const [isAvailable, setIsAvailable] = useState(false)
  const targetRef = useRef<DictationTarget | null>(null)
  const sessionRef = useRef<DictationSession | null>(null)
  /** Aborts a session that is still starting (token, permission prompt). */
  const startAbortRef = useRef<AbortController | null>(null)
  const generationRef = useRef(0)
  /** Set while speech is held: the latest text waits here. */
  const heldRef = useRef<DictatedText | null>(null)
  /** Holds in force (sends deciding, a mid-text caret); speech waits. */
  const holdCountRef = useRef(0)
  /** How many of those holds are sends. */
  const sendHoldCountRef = useRef(0)
  /** The session stopped while a send decided: the send's verdict ends it. */
  const isEndPendingRef = useRef(false)
  /**
   * The mark of the dictated text the editor last DISPLAYED — what an edit
   * makes the user's (see `rebase`). Held or not-yet-flushed words are not
   * in it.
   */
  const renderedMarkRef = useRef<TranscriptMark | null>(null)
  /** A stop is draining the trailing text; a second press waits for it. */
  const isStoppingRef = useRef(false)
  // `start` hands the session a way to stop itself; `stop` is defined after.
  const stopRef = useRef<() => void>(() => undefined)

  const renderText = useCallback(({ text, mark }: DictatedText) => {
    renderedMarkRef.current = mark
    targetRef.current?.render(text)
  }, [])

  const reset = useCallback(() => {
    // A failed start ends here too: a stale controller would read as a start
    // still in flight and swallow every later press.
    startAbortRef.current = null
    holdCountRef.current = 0
    sendHoldCountRef.current = 0
    isEndPendingRef.current = false
    heldRef.current = null
    renderedMarkRef.current = null
    isStoppingRef.current = false
    sessionRef.current = null
    targetRef.current?.end()
    setStatus('idle')
  }, [])

  const cancel = useCallback(() => {
    generationRef.current += 1
    startAbortRef.current?.abort()
    startAbortRef.current = null
    const session = sessionRef.current
    sessionRef.current = null
    session?.cancel()
    reset()
  }, [reset])

  const start = useCallback(() => {
    const target = targetRef.current
    // Already listening, or a start is still in flight.
    if (!target || sessionRef.current || startAbortRef.current) {
      return
    }
    generationRef.current += 1
    const generation = generationRef.current
    const isCurrent = () => generationRef.current === generation
    const startAbort = new AbortController()
    startAbortRef.current = startAbort
    // A hold belongs to the session it was taken under, never to this one.
    holdCountRef.current = 0
    sendHoldCountRef.current = 0
    heldRef.current = null
    renderedMarkRef.current = null
    setStatus('starting')
    target.begin()
    import('@/lib/dictation/stream-dictation')
      .then(({ startDictation }) =>
        // Cancelled while the chunk loaded: never touch the microphone.
        isCurrent()
          ? startDictation({
              signal: startAbort.signal,
              onText: (text, mark) => {
                if (!isCurrent()) {
                  return
                }
                if (holdCountRef.current > 0) {
                  heldRef.current = { text, mark }
                  return
                }
                renderText({ text, mark })
              },
              onError: (error) => {
                if (!isCurrent()) {
                  return
                }
                reportBrowserError(error, { action: 'chat_dictation' })
                appToast.error('Dictation stopped unexpectedly.')
                cancel()
              },
              onEnded: (reason) => {
                if (!isCurrent()) {
                  return
                }
                if (reason === 'limit') {
                  appToast.warning(
                    `Dictation stops after ${MAX_DICTATION_SESSION_MINUTES} minutes. Press the mic to keep going.`
                  )
                } else if (reason === 'device') {
                  appToast.warning('The microphone was disconnected.')
                }
                stopRef.current()
              },
            })
          : null
      )
      .then((session) => {
        if (session === null) {
          return
        }
        if (!isCurrent()) {
          session.cancel()
          return
        }
        startAbortRef.current = null
        sessionRef.current = session
        setStatus('listening')
      })
      .catch((error: unknown) => {
        if (!isCurrent()) {
          return
        }
        const message = describeStartError(error)
        if (message) {
          appToast.error(message)
        } else {
          reportBrowserError(error, { action: 'chat_dictation' })
          appToast.error('Could not start dictation.')
        }
        reset()
      })
  }, [cancel, renderText, reset])

  // The session is over. Speech a send is still deciding on waits for its
  // verdict; speech only the caret held back lands now — no later frame
  // would bring it.
  const endSession = useCallback(() => {
    if (sendHoldCountRef.current > 0) {
      isEndPendingRef.current = true
      return
    }
    const held = heldRef.current
    if (held !== null) {
      renderText(held)
    }
    reset()
  }, [renderText, reset])

  const stop = useCallback(() => {
    const session = sessionRef.current
    if (!session) {
      // Still starting: nothing has been said yet.
      cancel()
      return
    }
    if (isStoppingRef.current) {
      return
    }
    isStoppingRef.current = true
    const generation = generationRef.current
    setStatus('finishing')
    session.stop().finally(() => {
      if (generationRef.current === generation) {
        endSession()
      }
    })
  }, [cancel, endSession])
  stopRef.current = stop

  const hold = useCallback(
    (reason: DictationHoldReason): DictationHold => {
      if (sessionRef.current === null && startAbortRef.current === null) {
        // Nothing is listening: there is no speech to hold back.
        return SETTLED_HOLD
      }
      const generation = generationRef.current
      const isSend = reason === 'send'
      holdCountRef.current += 1
      if (isSend) {
        sendHoldCountRef.current += 1
      }
      let isSettled = false
      // Once per handle, and only for the session it was taken under.
      const settle = () => {
        if (isSettled || generationRef.current !== generation) {
          return false
        }
        isSettled = true
        holdCountRef.current = Math.max(0, holdCountRef.current - 1)
        if (isSend) {
          sendHoldCountRef.current = Math.max(0, sendHoldCountRef.current - 1)
        }
        return true
      }
      return {
        release: () => {
          if (!settle()) {
            return
          }
          if (isEndPendingRef.current) {
            // The session already stopped: the last send's verdict ends it.
            if (sendHoldCountRef.current === 0) {
              endSession()
            }
            return
          }
          // Another hold is still in force: the prompt must stay as it was.
          if (holdCountRef.current > 0) {
            return
          }
          const held = heldRef.current
          heldRef.current = null
          if (held !== null) {
            renderText(held)
          }
        },
        cancel: () => {
          if (settle()) {
            cancel()
          }
        },
      }
    },
    [cancel, endSession, renderText]
  )

  const rebase = useCallback(() => {
    // The user's edit is now the prompt, including the dictated text it
    // displayed. Held or not-yet-shown words are not theirs yet: the session
    // re-sends them after the edit, so the stale held copy goes.
    heldRef.current = null
    const displayed = renderedMarkRef.current
    renderedMarkRef.current = null
    sessionRef.current?.freeze(displayed)
  }, [])

  const registerTarget = useCallback(
    (target: DictationTarget | null) => {
      if (target === null && targetRef.current !== null) {
        // The editor is going away (e.g. swapped for the read-only box while
        // a turn streams): nowhere left to write, so stop listening.
        cancel()
      }
      targetRef.current = target
      setIsAvailable(target !== null)
    },
    [cancel]
  )

  // Browser resource lifecycle: an unmounting composer must release the
  // microphone and close the transcription socket.
  useEffect(
    () => () => {
      generationRef.current += 1
      startAbortRef.current?.abort()
      sessionRef.current?.cancel()
      sessionRef.current = null
    },
    []
  )

  return useMemo(
    () => ({
      status,
      isAvailable,
      start,
      stop,
      cancel,
      hold,
      rebase,
      registerTarget,
    }),
    [cancel, hold, isAvailable, rebase, registerTarget, start, status, stop]
  )
}
